import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import HttpIcon from '@material-ui/icons/Http';
import { CatalogIcon, DocsIcon, useContent } from '@backstage/core-components';
import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import { OpenApiSearchResultListItemExtension } from './OpenApiSearchResultListItem';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';
import {
  SearchBar,
  SearchResult,
  SearchResultPager,
  useSearch,
} from '@backstage/plugin-search-react';
import { PreSearchEmptyState } from './PreSearchEmptyState';

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    gap: theme.spacing(1),
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1fr auto',
    '&> button': {
      marginTop: theme.spacing(1),
    },
  },
  input: {
    flex: 1,
  },
  button: {
    '&:hover': {
      background: 'none',
    },
  },
  dialogActionsContainer: { padding: theme.spacing(1, 3) },
}));

// Quick filters: one click narrows the spotlight to a kind of thing. Kinds
// live inside the software-catalog document type as a `kind` filter; Docs is
// its own document type.
const FILTER_CHIPS: {
  label: string;
  types?: string[];
  kind?: string;
}[] = [
  { label: 'All' },
  { label: 'Components', types: ['software-catalog'], kind: 'Component' },
  { label: 'APIs', types: ['software-catalog'], kind: 'API' },
  { label: 'Systems', types: ['software-catalog'], kind: 'System' },
  { label: 'People', types: ['software-catalog'], kind: 'User' },
  { label: 'Teams', types: ['software-catalog'], kind: 'Group' },
  { label: 'Endpoints', types: ['openapi-endpoint'] },
  { label: 'Docs', types: ['techdocs'] },
];

const QuickFilters = () => {
  const { types, filters, setTypes, setFilters } = useSearch();
  const activeKind = filters.kind as string | undefined;

  const isActive = (chip: (typeof FILTER_CHIPS)[number]) =>
    chip.label === 'All'
      ? types.length === 0 && !activeKind
      : types[0] === chip.types?.[0] && activeKind === chip.kind;

  const apply = (chip: (typeof FILTER_CHIPS)[number]) => {
    const reset = chip.label === 'All' || isActive(chip);
    setTypes(reset ? [] : chip.types ?? []);
    setFilters(prev => {
      const { kind, ...rest } = prev;
      return reset || !chip.kind ? rest : { ...rest, kind: chip.kind };
    });
  };

  return (
    <Box display="flex" gridGap={8} flexWrap="wrap" pb={1}>
      {FILTER_CHIPS.map(chip => {
        const active = isActive(chip);
        return (
          <Chip
            key={chip.label}
            label={chip.label}
            size="small"
            clickable
            color={active ? 'primary' : 'default'}
            variant={active ? 'default' : 'outlined'}
            style={{ margin: 0 }}
            onClick={() => apply(chip)}
          />
        );
      })}
    </Box>
  );
};

// Same layout as the stock search modal, except that while the query is empty
// it shows PreSearchEmptyState instead of firing an empty search that renders
// "Sorry, no results were found".
export const SearchModalContent = (props: { toggleModal: () => void }) => {
  const { toggleModal } = props;
  const classes = useStyles();
  const navigate = useNavigate();
  const { transitions } = useTheme();
  const { focusContent } = useContent();
  const { term } = useSearch();
  const searchBarRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    searchBarRef?.current?.focus();
  });

  const handleSearchResultClick = useCallback(() => {
    setTimeout(focusContent, transitions.duration.leavingScreen);
  }, [focusContent, transitions]);

  const handleSearchBarSubmit = useCallback(() => {
    const query = searchBarRef.current?.value ?? '';
    navigate(`/search?query=${encodeURIComponent(query)}`);
    handleSearchResultClick();
  }, [navigate, handleSearchResultClick]);

  return (
    <>
      <DialogTitle>
        <Box className={classes.dialogTitle}>
          <SearchBar
            className={classes.input}
            inputProps={{ ref: searchBarRef }}
            onSubmit={handleSearchBarSubmit}
          />
          <IconButton aria-label="close" onClick={toggleModal}>
            <CloseIcon />
          </IconButton>
        </Box>
        <QuickFilters />
      </DialogTitle>
      <DialogContent>
        {term ? (
          <>
            <Grid
              container
              direction="row-reverse"
              justifyContent="flex-start"
              alignItems="center"
            >
              <Grid item>
                <Button
                  className={classes.button}
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleSearchBarSubmit}
                  disableRipple
                >
                  View Full Results
                </Button>
              </Grid>
            </Grid>
            <Divider />
            {/* Everything the index knows: catalog entities (services, APIs,
                systems, people, teams), individual OpenAPI endpoints, and
                TechDocs pages, with icons. */}
            <SearchResult
              onClick={handleSearchResultClick}
              onKeyDown={handleSearchResultClick}
            >
              <CatalogSearchResultListItem icon={<CatalogIcon />} />
              <OpenApiSearchResultListItemExtension icon={<HttpIcon />} />
              <TechDocsSearchResultListItem icon={<DocsIcon />} />
            </SearchResult>
          </>
        ) : (
          <PreSearchEmptyState />
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActionsContainer}>
        <Grid container direction="row">
          <Grid item xs={12}>
            {term && <SearchResultPager />}
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
};
